import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, Play, TrendingUp, Sparkles, ArrowRight, Users, Headphones, Disc, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import TrackCard from '@/components/TrackCard';
import AlbumCard from '@/components/AlbumCard';
import { MusicNoteBubbleBackground, GlowOrb, BubbleBackground} from '@/components/BubbleCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import RevenueRaceWidget from '@/components/RevenueRaceWidget';
import { useAuth } from '@/hooks/useAuth';
import { apiClient, formatApiError, resolveApiUrl, normalizeApiList } from '@/lib/apiClient';
import { artistAvatarSrc } from '@/lib/artistAvatar';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const floatVariants = {
  animate: (i) => ({
    y: [-10, 10, -10],
    transition: {
      duration: 5 + i,
      repeat: Infinity,
      ease: "easeInOut"
    }
  })
};

const isPublicItem = (item) => {
  const status = String(item?.status || 'published').toLowerCase();
  return status === 'published' || status === 'public';
};

const Home = () => {
  const { isAuthenticated } = useAuth();
  const primaryCtaTo = isAuthenticated ? '/browse' : '/login';
  const [newReleases, setNewReleases] = useState([]);
  const [newAlbumsThisWeek, setNewAlbumsThisWeek] = useState([]);
  const [topTracks, setTopTracks] = useState([]);
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [featuredAlbums, setFeaturedAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publicStats, setPublicStats] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [feedbackImage, setFeedbackImage] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState('');
  const [contestPayload, setContestPayload] = useState(null);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    try {
      const [tracksResp, albumsResp, artistsResp, statsResp, contestResp] = await Promise.all([
        apiClient.get('/api/tracks?limit=60'),
        apiClient.get('/api/albums?limit=40'),
        apiClient.get('/api/artists?limit=40'),
        apiClient.get('/api/public/stats').catch(() => ({ data: null })),
        apiClient.get('/api/contests/current').catch(() => ({ data: null }))
      ]);
      setContestPayload(contestResp?.data?.contest ? contestResp.data : null);

      const getTotalFromResp = (payload, fallback = 0) => {
        const p = payload;
        const candidates = [
          p?.total,
          p?.count,
          p?.total_count,
          p?.totalCount,
          p?.meta?.total,
          p?.meta?.count,
          p?.pagination?.total,
          p?.pagination?.count
        ];
        const n = candidates.map((v) => Number(v)).find((v) => Number.isFinite(v) && v >= 0);
        return Number.isFinite(n) ? n : fallback;
      };

      const tracks = normalizeApiList(tracksResp.data)
        .filter(isPublicItem)
        .map((t) => ({
          ...t,
          preview_url: resolveApiUrl(t?.preview_url),
          file_url: resolveApiUrl(t?.file_url),
          cover_url: resolveApiUrl(t?.cover_url)
        }));
      const albums = normalizeApiList(albumsResp.data)
        .filter(isPublicItem)
        .map((a) => ({
          ...a,
          cover_url: resolveApiUrl(a?.cover_url)
        }));
      const artists = normalizeApiList(artistsResp.data).map((a) => ({
        ...a,
        picture: artistAvatarSrc(a)
      }));

      const weekMs = 7 * 24 * 60 * 60 * 1000;
      const weekCutoff = Date.now() - weekMs;
      const createdMs = (iso) => {
        if (!iso) return 0;
        const t = Date.parse(iso);
        return Number.isFinite(t) ? t : 0;
      };

      const tracksThisWeek = tracks
        .filter((t) => createdMs(t.created_at) >= weekCutoff)
        .sort((a, b) => createdMs(b.created_at) - createdMs(a.created_at))
        .slice(0, 6);
      setNewReleases(tracksThisWeek);

      setTopTracks(
        [...tracks].sort((a, b) => (b.play_count || 0) - (a.play_count || 0)).slice(0, 6)
      );

      const albumsWeek = albums
        .filter((a) => createdMs(a.created_at) >= weekCutoff)
        .sort((a, b) => createdMs(b.created_at) - createdMs(a.created_at))
        .slice(0, 8);
      setNewAlbumsThisWeek(albumsWeek);

      const playsForAlbum = (albumId) =>
        tracks
          .filter((t) => t.album_id === albumId)
          .reduce((s, t) => s + (Number(t.play_count) || 0), 0);

      setFeaturedAlbums(
        [...albums].sort((a, b) => playsForAlbum(b.album_id) - playsForAlbum(a.album_id)).slice(0, 8)
      );
      setFeaturedArtists(artists.slice(0, 8));

      // Prefer backend aggregated stats, but fall back to list endpoint totals when available.
      const apiStats = statsResp?.data && typeof statsResp.data === 'object' ? statsResp.data : {};
      const fallbackTracks = getTotalFromResp(tracksResp.data, tracks.length);
      const fallbackArtists = getTotalFromResp(artistsResp.data, artists.length);
      const fallbackUsers =
        Number(apiStats?.users_count) ||
        Number(apiStats?.users) ||
        0;

      setPublicStats({
        ...apiStats,
        tracks_count: Number(apiStats?.tracks_count ?? apiStats?.tracks) || fallbackTracks,
        artists_count: Number(apiStats?.artists_count ?? apiStats?.artists) || fallbackArtists,
        users_count: Number(apiStats?.users_count ?? apiStats?.users) || fallbackUsers
      });
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    const cleanTitle = feedbackTitle.trim();
    const cleanDescription = feedbackDescription.trim();
    if (!cleanTitle || !cleanDescription) return;

    setFeedbackSubmitting(true);
    setFeedbackError('');
    setFeedbackSuccess('');
    try {
      const formData = new FormData();
      formData.append('title', cleanTitle);
      formData.append('description', cleanDescription);
      if (feedbackImage) formData.append('image', feedbackImage);
      await apiClient.post('/api/feedback', formData);
      setFeedbackSuccess('Merci ! Ton retour a bien ete envoye.');
      setFeedbackTitle('');
      setFeedbackDescription('');
      setFeedbackImage(null);
    } catch (err) {
      setFeedbackError(formatApiError(err));
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <BubbleBackground />


      {/* Glow Orbs */}
      <GlowOrb color="cyan" size={600} x="20%" y="30%" blur={150} />
      <GlowOrb color="purple" size={500} x="80%" y="60%" blur={120} />
      <GlowOrb color="pink" size={400} x="50%" y="90%" blur={100} />

      {/* Hero Section */}
      <section className="relative min-h-[100vh] flex items-center px-4 md:px-8 lg:px-12 overflow-hidden pt-24 pb-16">
        <div
          className={`relative z-10 w-full max-w-7xl mx-auto grid gap-10 lg:gap-12 items-center ${
            contestPayload?.contest ? 'lg:grid-cols-2' : 'justify-items-center'
          }`}
        >
          <div className={`space-y-8 ${contestPayload?.contest ? 'text-left' : 'text-center max-w-3xl'}`}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-6"
          >
            <motion.div
              custom={2}
              variants={floatVariants}
              animate="animate"
              className="inline-block"
            >
              <Badge 
                className="px-6 py-2 text-sm bg-primary/10 border border-primary/30 text-primary rounded-full backdrop-blur-md"
                data-testid="hero-badge"
              >
                <Headphones className="w-4 h-4 mr-2 inline" />
                La musique vous appartient
              </Badge>
            </motion.div>

            <h1 
              className="text-5xl sm:text-6xl md:text-7xl xl:text-8xl font-bold tracking-tighter leading-none"
              data-testid="hero-title"
            >
              <span className="text-foreground">Possédez votre</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 text-glow-cyan">
                musique
              </span>
            </h1>
            
            <p 
              className={`text-lg md:text-xl text-muted-foreground leading-relaxed ${contestPayload?.contest ? 'max-w-xl' : 'max-w-2xl mx-auto'}`}
              data-testid="hero-description"
            >
              Kloud réinvente l'achat de musique numérique. Achetez, téléchargez et gardez vos titres préférés pour toujours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className={`flex flex-col sm:flex-row gap-4 ${contestPayload?.contest ? '' : 'justify-center'}`}
          >
            <Link to={primaryCtaTo} data-testid="hero-register-link">
              <Button 
                size="lg" 
                className="rounded-full px-10 py-7 text-lg bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 border-0 shadow-[0_0_30px_rgba(34,211,238,0.3)] hover:shadow-[0_0_40px_rgba(34,211,238,0.5)] transition-shadow"
                data-testid="hero-register-button"
              >
                <Play className="w-5 h-5 mr-2" />
                Commencer gratuitement
              </Button>
            </Link>
            
            <Link to="/browse" data-testid="hero-browse-link">
              <Button 
                size="lg" 
                variant="outline" 
                className="rounded-full px-10 py-7 text-lg glass border-white/20 hover:bg-white/10 hover:border-white/30"
                data-testid="hero-browse-button"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Découvrir
              </Button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="pt-2"
            data-testid="hero-stats"
          >
            <div className={`flex items-center gap-4 sm:gap-5 flex-wrap ${contestPayload?.contest ? '' : 'justify-center'}`}>
              {(() => {
                const nf = new Intl.NumberFormat('fr-FR');
                const artistsCount =
                  Number(publicStats?.artists_count) ||
                  Number(publicStats?.artists) || 0;
                const tracksCount =
                  Number(publicStats?.tracks_count) ||
                  Number(publicStats?.tracks) || 0;
                const usersCount =
                  Number(publicStats?.users_count) ||
                  Number(publicStats?.users) || 0;

                const cards = [
                  {
                    id: 'stat-tracks',
                    value: nf.format(tracksCount),
                    label: 'Tracks',
                    Icon: Music,
                    gradient: 'from-cyan-400 to-purple-400'
                  },
                  {
                    id: 'stat-artists',
                    value: nf.format(artistsCount),
                    label: 'Artistes',
                    Icon: TrendingUp,
                    gradient: 'from-purple-400 to-pink-400'
                  },
                  {
                    id: 'stat-users',
                    value: nf.format(usersCount),
                    label: 'Users',
                    Icon: Users,
                    gradient: 'from-pink-400 to-cyan-400'
                  }
                ];

                return cards.map((card, idx) => (
                  <motion.div
                    key={card.id}
                    custom={idx}
                    variants={floatVariants}
                    animate="animate"
                    className="group relative rounded-2xl px-5 py-3.5 text-center min-w-[132px] border backdrop-blur-md bg-white/95 border-slate-200/90 shadow-md shadow-slate-900/5 dark:bg-white/[0.04] dark:border-white/10 dark:shadow-[0_0_30px_rgba(0,0,0,0.28)]"
                  >
                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-b from-slate-100/80 to-transparent dark:from-white/[0.06] dark:to-transparent" />
                    <div className="relative flex items-center justify-center gap-2">
                      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-xl bg-gradient-to-br ${card.gradient} opacity-90`}>
                        <card.Icon className="w-3.5 h-3.5 text-zinc-900/90 dark:text-black/80" />
                      </span>
                      <div className={`text-2xl md:text-3xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r ${card.gradient}`}>
                        {card.value}
                      </div>
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/55">
                      {card.label}
                    </div>
                  </motion.div>
                ));
              })()}
            </div>
            {/* Widget Beta feedback — masqué pour l’instant */}
          </motion.div>
          </div>

          {contestPayload?.contest && (
            <motion.div
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="w-full max-w-xl mx-auto lg:mx-0 lg:justify-self-end"
            >
              <RevenueRaceWidget
                contest={contestPayload.contest}
                leaderboard={contestPayload.leaderboard}
                series={contestPayload.series}
                days={contestPayload.days}
                compact
              />
            </motion.div>
          )}
        </div>

        {/* Scroll Indicator */}
        {/* <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </motion.div> */}
      </section>

      {/* New Releases - Floating Cards */}
      <section className="relative py-16 px-6 md:px-12">
        <GlowOrb color="cyan" size={400} x="0%" y="50%" blur={100} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="flex items-end justify-between mb-12">
              <div>
                <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                  Fraîchement sorti
                </Badge>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight">
                  Nouveautés
                </h2>
                <p className="text-muted-foreground mt-2 text-lg">
                  Sorties des 7 derniers jours (tracks)
                </p>
              </div>
              <Link to="/browse">
                <Button variant="ghost" className="rounded-full glass hover:bg-white/10">
                  Tout voir <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-square rounded-3xl glass animate-pulse" />
                ))}
              </div>
            ) : newReleases.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Aucune sortie cette semaine pour l’instant.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {newReleases.map((track, index) => (
                  <motion.div
                    key={track.track_id}
                    variants={itemVariants}
                    custom={index}
                    className={`animate-float-gentle stagger-${index + 1}`}
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <TrackCard track={track} />
                  </motion.div>
                ))}
              </div>
            )}
            {!loading && newAlbumsThisWeek.length > 0 && (
              <>
                <motion.h3 variants={itemVariants} className="text-2xl font-bold mt-14 mb-6">
                  Albums de la semaine
                </motion.h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {newAlbumsThisWeek.map((album, index) => (
                    <motion.div key={album.album_id} variants={itemVariants} className={`animate-float-slow stagger-${(index % 4) + 1}`}>
                      <AlbumCard album={album} />
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Top Tracks */}
      <section className="relative py-16 px-6 md:px-12">
        <GlowOrb color="purple" size={500} x="90%" y="30%" blur={120} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="flex items-center gap-4 mb-12">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center border border-purple-500/30">
                <TrendingUp className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight">
                  Top du moment
                </h2>
                <p className="text-muted-foreground text-lg">Classés par nombre d’écoutes</p>
              </div>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {topTracks.map((track, index) => (
                <motion.div 
                  key={track.track_id} 
                  variants={itemVariants}
                  className="relative"
                >
                  <Badge 
                    className="absolute -top-3 -left-3 z-20 w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 border-0 text-white font-bold shadow-lg"
                  >
                    {index + 1}
                  </Badge>
                  <TrackCard track={track} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Albums */}
      <section className="relative py-16 px-6 md:px-12">
        <GlowOrb color="pink" size={400} x="20%" y="60%" blur={100} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="flex items-center gap-4 mb-12">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500/20 to-orange-500/20 flex items-center justify-center border border-pink-500/30 animate-breathe">
                <Disc className="w-8 h-8 text-pink-400" />
              </div>
              <div>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight">
                  Albums
                </h2>
                <p className="text-muted-foreground text-lg">Les plus écoutés (somme des écoutes des titres)</p>
              </div>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {featuredAlbums.map((album, index) => (
                <motion.div 
                  key={album.album_id}
                  variants={itemVariants}
                  className={`animate-float-slow stagger-${(index % 4) + 1}`}
                >
                  <AlbumCard album={album} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Artists - Circular Bubbles */}
      <section className="relative py-16 px-6 md:px-12">
        <GlowOrb color="cyan" size={500} x="70%" y="40%" blur={120} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="flex items-center gap-4 mb-12">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center border border-cyan-500/30 animate-glow-pulse">
                <Star className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight">
                  Artistes
                </h2>
                <p className="text-muted-foreground text-lg">Les talents de la plateforme</p>
              </div>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {featuredArtists.map((artist, index) => (
                <motion.div
                  key={artist.user_id}
                  variants={itemVariants}
                  custom={index}
                >
                  <Link to={`/artist/${artist.user_id}`}>
                    <motion.div
                      className="group relative text-center"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative mx-auto w-40 h-40 md:w-48 md:h-48">
                        {/* Glow ring */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-500/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        
                        <Avatar className="w-full h-full border-2 border-white/10 group-hover:border-cyan-400/50 transition-colors duration-300 shadow-2xl">
                          <AvatarImage src={artist.picture} className="object-cover" />
                          <AvatarFallback className="text-4xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20">
                            {artist.artist_name?.[0] || artist.name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      
                      <div className="mt-4 space-y-1">
                        <h3 className="font-semibold text-lg group-hover:text-cyan-400 transition-colors">
                          {artist.artist_name || artist.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">Artiste</p>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Kloud - Glass Cards */}
      <section className="relative py-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
                Pourquoi <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Kloud</span> ?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Une nouvelle approche de la musique numérique
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Music,
                  title: "Vraie propriété",
                  description: "Achetez une fois, gardez pour toujours. Téléchargez vos titres et écoutez-les même hors ligne.",
                  color: "cyan",
                  testId: "feature-ownership"
                },
                {
                  icon: TrendingUp,
                  title: "Soutenez les artistes",
                  description: "Les artistes reçoivent une part équitable. Pas d'intermédiaires, juste vous et la musique.",
                  color: "purple",
                  testId: "feature-artists"
                },
                {
                  icon: Sparkles,
                  title: "Haute qualité",
                  description: "Fichiers audio haute qualité, sans compression. La musique comme l'artiste l'a voulue.",
                  color: "pink",
                  testId: "feature-quality"
                }
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  variants={itemVariants}
                  custom={index}
                  className={`animate-float-gentle stagger-${index + 1}`}
                  data-testid={feature.testId}
                >
                  <motion.div
                    className="h-full p-8 rounded-3xl glass-heavy hover:border-white/20 transition-colors duration-300"
                    whileHover={{ y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className={`w-14 h-14 rounded-2xl bg-${feature.color}-500/10 flex items-center justify-center mb-6 border border-${feature.color}-500/30`}>
                      <feature.icon className={`w-7 h-7 text-${feature.color}-400`} />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 px-6 md:px-12">
        <GlowOrb color="cyan" size={600} x="50%" y="50%" blur={150} />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8 glass-heavy rounded-[3rem] p-12 md:p-16"
          >
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight">
              Prêt à <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">découvrir</span> ?
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Rejoignez des milliers de mélomanes qui ont choisi de posséder leur musique
            </p>
            <Link to="/register" data-testid="cta-register-link">
              <Button 
                size="lg" 
                className="rounded-full px-12 py-7 text-lg bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 border-0 shadow-[0_0_40px_rgba(34,211,238,0.4)] hover:shadow-[0_0_50px_rgba(34,211,238,0.6)] transition-shadow"
                data-testid="cta-register-button"
              >
                Créer mon compte gratuitement
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Spacer for audio player */}
      <div className="h-32" />

      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent className="glass-heavy border-white/15 rounded-2xl max-w-xl">
          <DialogHeader>
            <DialogTitle>Envoyer un retour beta</DialogTitle>
            <DialogDescription>
              Ton message est envoye directement au backend puis visible dans l'admin.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitFeedback} className="space-y-4">
            {feedbackSuccess && (
              <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
                {feedbackSuccess}
              </div>
            )}
            {feedbackError && (
              <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                {feedbackError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="feedback-title">Titre</Label>
              <Input
                id="feedback-title"
                value={feedbackTitle}
                onChange={(e) => setFeedbackTitle(e.target.value)}
                placeholder="Ex: Bug navigation mobile"
                disabled={feedbackSubmitting}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-description">Description</Label>
              <Textarea
                id="feedback-description"
                value={feedbackDescription}
                onChange={(e) => setFeedbackDescription(e.target.value)}
                placeholder="Explique ce que tu as vu, etapes, resultat attendu..."
                rows={6}
                disabled={feedbackSubmitting}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-image">Image (optionnel)</Label>
              <Input
                id="feedback-image"
                type="file"
                accept="image/*"
                onChange={(e) => setFeedbackImage(e.target.files?.[0] || null)}
                disabled={feedbackSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                Optionnel. L'image est envoyee au backend avec ton retour.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setFeedbackOpen(false)} disabled={feedbackSubmitting}>
                Annuler
              </Button>
              <Button type="submit" className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 border-0" disabled={feedbackSubmitting}>
                {feedbackSubmitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
